"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import {
  emptyGpxValue,
  generateGpxXml,
  gpxLastFixLabel,
  gpxPointCount,
  parseGpxValue,
  type GpxFieldValue,
  type GpxPoint,
} from "../utils/gpxValue";
import { GpxMapModal } from "./GpxMapModal";

const GPS_INTERVAL_MS = 5000;

type Props = {
  value: unknown;
  fieldName: string;
  label?: string;
  itemId?: string;
  collectionSlug?: string;
  onChange: (next: GpxFieldValue) => void;
  onAppendPoint?: (point: GpxPoint) => Promise<void> | void;
  onStartRecording?: () => Promise<void> | void;
  onStopRecording?: () => Promise<void> | void;
  disabled?: boolean;
};

export function GpxFieldControl({
  value,
  fieldName,
  label,
  onChange,
  onAppendPoint,
  onStartRecording,
  onStopRecording,
  disabled = false,
}: Props) {
  const current = parseGpxValue(value) ?? emptyGpxValue();
  const valueRef = useRef(current);
  valueRef.current = current;
  const watchIdRef = useRef<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mapOpen, setMapOpen] = useState(false);

  const stopWatch = useCallback(() => {
    if (watchIdRef.current != null && typeof navigator !== "undefined") {
      navigator.geolocation.clearWatch(watchIdRef.current);
      watchIdRef.current = null;
    }
  }, []);

  useEffect(() => () => stopWatch(), [stopWatch]);

  const startRecording = useCallback(async () => {
    setError(null);
    if (typeof navigator === "undefined" || !navigator.geolocation) {
      setError("Geolocation is not available in this browser.");
      return;
    }

    const started: GpxFieldValue = {
      ...valueRef.current,
      isRecording: true,
      recordingStarted: new Date().toISOString(),
    };
    valueRef.current = started;
    onChange(started);
    try {
      await onStartRecording?.();
    } catch {
      // local state already started
    }

    watchIdRef.current = navigator.geolocation.watchPosition(
      (pos) => {
        const point: GpxPoint = {
          lat: pos.coords.latitude,
          lon: pos.coords.longitude,
          ele: pos.coords.altitude ?? undefined,
          time: new Date(pos.timestamp).toISOString(),
        };
        const next: GpxFieldValue = {
          ...valueRef.current,
          track: [...valueRef.current.track, point],
          isRecording: true,
        };
        valueRef.current = next;
        onChange(next);
        void onAppendPoint?.(point);
      },
      (err) => {
        setError(err.message || "Location permission denied.");
        stopWatch();
        const stopped = { ...valueRef.current, isRecording: false };
        valueRef.current = stopped;
        onChange(stopped);
      },
      {
        enableHighAccuracy: true,
        maximumAge: GPS_INTERVAL_MS,
        timeout: 15000,
      }
    );
  }, [onAppendPoint, onChange, onStartRecording, stopWatch]);

  const stopRecording = useCallback(async () => {
    stopWatch();
    const next = { ...valueRef.current, isRecording: false };
    valueRef.current = next;
    onChange(next);
    try {
      await onStopRecording?.();
    } catch {
      // ignore
    }
  }, [onChange, onStopRecording, stopWatch]);

  const exportGpx = useCallback(() => {
    const xml = generateGpxXml(valueRef.current, label || fieldName);
    const blob = new Blob([xml], { type: "application/gpx+xml" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${fieldName}.gpx`;
    a.click();
    URL.revokeObjectURL(url);
  }, [fieldName, label]);

  const count = gpxPointCount(current);
  const lastLabel = gpxLastFixLabel(current);

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-slate-700 dark:text-slate-200">
        <span>
          {current.isRecording
            ? `Recording — ${count} pts`
            : count > 0
              ? `${count} pts recorded`
              : "No track yet"}
        </span>
        {lastLabel ? (
          <span className="font-mono text-xs text-slate-500">{lastLabel}</span>
        ) : null}
      </div>
      {error ? <p className="text-sm text-red-600">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        {!disabled &&
          (current.isRecording ? (
            <Button type="button" variant="secondary" size="sm" onClick={() => void stopRecording()}>
              Stop
            </Button>
          ) : (
            <Button type="button" variant="destructive" size="sm" onClick={() => void startRecording()}>
              Record
            </Button>
          ))}
        {count > 0 ? (
          <Button type="button" variant="outline" size="sm" onClick={() => setMapOpen(true)}>
            Map
          </Button>
        ) : null}
        {count > 0 ? (
          <Button type="button" variant="outline" size="sm" onClick={exportGpx}>
            Export GPX
          </Button>
        ) : null}
      </div>
      <GpxMapModal
        open={mapOpen}
        value={current}
        title={label || fieldName}
        onClose={() => setMapOpen(false)}
      />
    </div>
  );
}
